"use strict";
/**
 * Public barrel for the Settings editor's pure helpers. The implementation
 * is intentionally split across focused modules:
 *
 *  - {@link ./ai-hub-settings-safe-records}  prototype-pollution guards,
 *    string clamps, URL scheme allowlist.
 *  - {@link ./ai-hub-settings-record-entries}  index-addressed Record
 *    mutators (used by the mapping editor).
 *  - {@link ./ai-hub-settings-factories}  blank-row factories tagged with
 *    fresh `__clientId`s.
 *  - {@link ./ai-hub-settings-sanitization}  schema-level normalizer used
 *    by the equality helpers and on save.
 *  - {@link ./ai-hub-settings-equality}  structural-equality helpers
 *    powering change-detection.
 *
 * Existing importers continue to read from this module unchanged.
 */
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.reindexDraftIndexes = exports.restoreOrRemoveDraftItem = exports.cloneAiHubSettingsWorkflow = exports.cloneAiHubSettingsField = exports.cloneAiHubSettingsSystem = exports.cloneAiHubSettingsSchema = void 0;
var common_utils_1 = require("../../shared/utils/common-utils");
var ai_hub_settings_safe_records_1 = require("./ai-hub-settings-safe-records");
Object.defineProperty(exports, "isSafeRecordKey", { enumerable: true, get: function () { return ai_hub_settings_safe_records_1.isSafeRecordKey; } });
Object.defineProperty(exports, "safeRecord", { enumerable: true, get: function () { return ai_hub_settings_safe_records_1.safeRecord; } });
Object.defineProperty(exports, "cloneRecord", { enumerable: true, get: function () { return ai_hub_settings_safe_records_1.cloneRecord; } });
Object.defineProperty(exports, "clampUserString", { enumerable: true, get: function () { return ai_hub_settings_safe_records_1.clampUserString; } });
Object.defineProperty(exports, "sanitizeHttpUrl", { enumerable: true, get: function () { return ai_hub_settings_safe_records_1.sanitizeHttpUrl; } });
Object.defineProperty(exports, "sanitizeRecord", { enumerable: true, get: function () { return ai_hub_settings_safe_records_1.sanitizeRecord; } });
Object.defineProperty(exports, "MAX_USER_STRING_LENGTH", { enumerable: true, get: function () { return ai_hub_settings_safe_records_1.MAX_USER_STRING_LENGTH; } });
var ai_hub_settings_record_entries_1 = require("./ai-hub-settings-record-entries");
Object.defineProperty(exports, "appendRecordEntry", { enumerable: true, get: function () { return ai_hub_settings_record_entries_1.appendRecordEntry; } });
Object.defineProperty(exports, "updateRecordEntry", { enumerable: true, get: function () { return ai_hub_settings_record_entries_1.updateRecordEntry; } });
Object.defineProperty(exports, "removeRecordEntry", { enumerable: true, get: function () { return ai_hub_settings_record_entries_1.removeRecordEntry; } });
var ai_hub_settings_factories_1 = require("./ai-hub-settings-factories");
Object.defineProperty(exports, "createEmptySystem", { enumerable: true, get: function () { return ai_hub_settings_factories_1.createEmptySystem; } });
Object.defineProperty(exports, "createEmptyWorkflow", { enumerable: true, get: function () { return ai_hub_settings_factories_1.createEmptyWorkflow; } });
Object.defineProperty(exports, "createEmptyField", { enumerable: true, get: function () { return ai_hub_settings_factories_1.createEmptyField; } });
Object.defineProperty(exports, "createEmptyFieldMapping", { enumerable: true, get: function () { return ai_hub_settings_factories_1.createEmptyFieldMapping; } });
var ai_hub_settings_sanitization_1 = require("./ai-hub-settings-sanitization");
Object.defineProperty(exports, "sanitizeAiHubSettingsSchema", { enumerable: true, get: function () { return ai_hub_settings_sanitization_1.sanitizeAiHubSettingsSchema; } });
var ai_hub_settings_equality_1 = require("./ai-hub-settings-equality");
Object.defineProperty(exports, "areAiHubSettingsSchemasEqual", { enumerable: true, get: function () { return ai_hub_settings_equality_1.areAiHubSettingsSchemasEqual; } });
Object.defineProperty(exports, "hasAiHubSettingsSystemChanged", { enumerable: true, get: function () { return ai_hub_settings_equality_1.hasAiHubSettingsSystemChanged; } });
Object.defineProperty(exports, "hasAiHubSettingsFieldChanged", { enumerable: true, get: function () { return ai_hub_settings_equality_1.hasAiHubSettingsFieldChanged; } });
/**
 * Deep clone a settings schema.
 */
exports.cloneAiHubSettingsSchema = function (config) {
    return common_utils_1.deepClone(config);
};
/**
 * Clone a single settings system.
 */
exports.cloneAiHubSettingsSystem = function (system) {
    return common_utils_1.deepClone(system);
};
/**
 * Clone a single settings field.
 */
exports.cloneAiHubSettingsField = function (field) {
    return common_utils_1.deepClone(field);
};
/**
 * Clone a single settings workflow.
 */
exports.cloneAiHubSettingsWorkflow = function (workflow) { return common_utils_1.deepClone(workflow); };
/**
 * Restore a saved item at the given index or remove the draft item when
 * none exists. Used by the cancel-edit flows on Systems / Workflows /
 * Fields rows.
 */
exports.restoreOrRemoveDraftItem = function (items, index, savedItem) {
    var nextItems = __spreadArrays(items);
    if (savedItem) {
        nextItems[index] = common_utils_1.deepClone(savedItem);
        return nextItems;
    }
    nextItems.splice(index, 1);
    return nextItems;
};
/**
 * Reindex tracked draft indexes after removing an item.
 */
exports.reindexDraftIndexes = function (indexes, removedIndex) {
    return indexes
        .filter(function (currentIndex) { return currentIndex !== removedIndex; })
        .map(function (currentIndex) { return (currentIndex > removedIndex ? currentIndex - 1 : currentIndex); });
};
